import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { WindowElement } from '../types';
import { Camera, X, Image, ChevronLeft, ChevronRight } from 'lucide-react';
import PhotoCapture from './PhotoCapture';
import PhotoGalleryModal from './PhotoGalleryModal';
import { PhotoData } from '../services/CameraService';
import CameraService from '../services/CameraService';

interface WindowPhoto {
  id: string;
  fileName: string;
  localPath: string;
  url: string;
  timestamp: string;
  order: number;
}

interface WindowDimensionPromptProps {
  isOpen: boolean;
  windowElement: WindowElement | null;
  onSave: (dimensions: { 
    width: number; 
    height: number; 
    trickleVents: number; 
    isDraughtProofed: boolean; 
    windowType?: 'pvc' | 'wood' | 'metal';
    orientation?: string;
    glazingGap?: string;
    glazingType?: string;
    photos?: WindowPhoto[];
  }) => void;
  onDelete: () => void;
  onClose: () => void;
  isFullscreen?: boolean;
}

const WindowDimensionPrompt: React.FC<WindowDimensionPromptProps> = ({ isOpen, windowElement, onSave, onDelete, onClose, isFullscreen = false }) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [trickleVents, setTrickleVents] = useState(0);
  const [isDraughtProofed, setIsDraughtProofed] = useState(false);
  const [windowType, setWindowType] = useState<'pvc' | 'wood' | 'metal' | ''>('');
  const [selectedOrientation, setSelectedOrientation] = useState<string>('');
  
  // New glazing options
  const [glazingGap, setGlazingGap] = useState<string>('');
  const [glazingType, setGlazingType] = useState<string>('');
  const [photos, setPhotos] = useState<WindowPhoto[]>([]);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  // Available options
  const orientations = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  
  const glazingGapOptions = [
    '6 mm',
    '12 mm',
    '16 mm or more'
  ];

  const glazingTypeOptions = [
    'Single glazing',
    'Double pre 2002',
    'Double between 2002 and 2021',
    'Double post or during 2022',
    'Double with unknown install date',
    'Secondary glazing',
    'Triple pre 2002',
    'Triple between 2002 and 2021',
    'Triple post or during 2022',
    'Triple with unknown install date',
    'Single glazing, known data',
    'Double glazing, known data',
    'Triple glazing, known data'
  ];


  // Handle photo capture
  const handlePhotosCaptured = (newPhotos: PhotoData[]) => {
    console.log(`[WindowModal] Photos captured: ${newPhotos.length}`);
    
    // Convert PhotoData to WindowPhoto format
    const windowPhotos: WindowPhoto[] = newPhotos.map(photo => ({
      id: `window-photo-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      fileName: `window-photo-${Date.now()}-${Math.random().toString(36).substring(2, 9)}.jpg`,
      localPath: `data:image/${photo.format};base64,${photo.base64String}`,
      url: `data:image/${photo.format};base64,${photo.base64String}`,
      timestamp: new Date().toISOString(),
      order: photos.length
    }));
    
    // Add to existing photos
    setPhotos(prev => [...prev, ...windowPhotos]);
  };

  // Remove photo
  const removePhoto = (photoId: string) => {
    setPhotos(prev => prev.filter(photo => photo.id !== photoId));
  };

  // Render camera button
  const renderCameraButton = () => {
    const cameraService = CameraService.getInstance();
    const supportsCustomCamera = cameraService.supportsCustomCamera();
    
    if (supportsCustomCamera) {
      return (
        <PhotoCapture
          onPhotosCaptured={handlePhotosCaptured}
          maxPhotos={10}
          buttonLabel="Take Photos"
          buttonClass="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-500 rounded-lg hover:from-blue-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20 shadow-lg shadow-blue-500/25 transition-all duration-200"
        />
      );
    } else {
      return (
        <button
          type="button"
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-500 rounded-lg hover:from-blue-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20 shadow-lg shadow-blue-500/25 transition-all duration-200"
          onClick={() => console.warn('Camera not available on this device')}
        >
          <Camera className="w-4 h-4" />
          <span>Take Photos</span>
        </button>
      );
    }
  };

  // Format input to ensure proper decimal formatting
  const formatDecimalInput = (value: string): string => {
    if (!value) return '';
    
    // Remove any non-numeric characters except decimal point
    let cleanValue = value.replace(/[^0-9.]/g, '');
    
    // Handle multiple decimal points - keep only the first one
    const decimalIndex = cleanValue.indexOf('.');
    if (decimalIndex !== -1) {
      cleanValue = cleanValue.substring(0, decimalIndex + 1) + cleanValue.substring(decimalIndex + 1).replace(/\./g, '');
    }
    
    // Auto-add decimal point for common input patterns
    // If user types 3+ digits without decimal (like "120"), automatically add decimal
    if (!cleanValue.includes('.') && cleanValue.length >= 3) {
      // Insert decimal point before last 2 digits (120 -> 1.20)
      cleanValue = cleanValue.slice(0, -2) + '.' + cleanValue.slice(-2);
    }
    
    // Limit to 2 decimal places
    if (cleanValue.includes('.')) {
      const parts = cleanValue.split('.');
      if (parts[1] && parts[1].length > 2) {
        cleanValue = parts[0] + '.' + parts[1].substring(0, 2);
      }
    }
    
    return cleanValue;
  };

  useEffect(() => {
    if (windowElement) {
      // 🔧 ENHANCED: Populate fields with existing values when editing, empty for new windows
      // Convert from stored mm values to meters for display
      setStep(1);
      setWidth(windowElement.width ? (windowElement.width / 1000).toFixed(2) : '');
      setHeight(windowElement.height ? (windowElement.height / 1000).toFixed(2) : '');

      setTrickleVents(typeof windowElement.trickleVents === 'number' ? windowElement.trickleVents : 0);
      setIsDraughtProofed(windowElement.isDraughtProofed || false);
      setWindowType(windowElement.windowType || '');
      
      // Initialize orientation settings
      setSelectedOrientation(windowElement.orientation || '');
      
      // Initialize new glazing options
      setGlazingGap((windowElement as any).glazingGap || '');
      setGlazingType((windowElement as any).glazingType || '');
      setPhotos([]);
      
      console.log('🪟 Window edit dialog populated with existing values:', {
        storedWidthMm: windowElement.width,
        storedHeightMm: windowElement.height,
        displayWidthM: windowElement.width ? (windowElement.width / 1000).toFixed(2) + 'm' : 'N/A',
        displayHeightM: windowElement.height ? (windowElement.height / 1000).toFixed(2) + 'm' : 'N/A',
        trickleVents: windowElement.trickleVents,
        isDraughtProofed: windowElement.isDraughtProofed,
        windowType: windowElement.windowType,
        orientation: windowElement.orientation,
        glazingGap: (windowElement as any).glazingGap,
        glazingType: (windowElement as any).glazingType,
      });
    }
  }, [windowElement]);

  if (!isOpen || !windowElement) {
    return null;
  }

  const handleSave = () => {
    // Convert from meters input to mm for storage (maintaining compatibility)
    const dimensions = {
      width: (parseFloat(width) || 0) * 1000,
      height: (parseFloat(height) || 0) * 1000,
      trickleVents,
      isDraughtProofed,
      windowType: windowType || undefined,
      orientation: selectedOrientation || undefined,
      glazingGap: glazingGap || undefined,
      glazingType: glazingType || undefined,
      photos: photos
    };
    
    console.log('🪟 Window dimensions save:', {
      inputMeters: { width, height },
      storedMm: { width: dimensions.width, height: dimensions.height },
      isDraughtProofed,
      windowType,
      glazingGap,
      glazingType,
      photoCount: photos.length
    });
    
    onSave(dimensions);
    onClose();
  };

  const handleDelete = () => {
    onDelete();
  };

  // Handle next step
  const handleNextStep = () => {
    if (!width || !height) {
      alert('Please enter window dimensions');
      return;
    }
    setStep(2);
  };

  // Handle delete photo (for gallery)
  const handleDeletePhoto = (photoId: string) => {
    setPhotos(prev => prev.filter(photo => photo.id !== photoId));
  };

  // Handle update note (for gallery)
  const handleUpdateNote = (photoId: string, note: string) => {
    setPhotos(prev => prev.map(photo =>
      photo.id === photoId ? { ...photo, note } as WindowPhoto : photo
    ));
  };

  const handleSkip = () => {
    const dimensions = {
      width: (parseFloat(width) || 0) * 1000,
      height: (parseFloat(height) || 0) * 1000,
      trickleVents,
      isDraughtProofed,
      windowType: windowType || undefined,
      orientation: selectedOrientation || undefined,
      glazingGap: glazingGap || undefined,
      glazingType: glazingType || undefined,
      photos: []
    };
    onSave(dimensions);
    onClose();
  };

  // Modern dropdown component with icons and enhanced styling
  const ModernDropdown = ({ label, value, options, onChange, placeholder, icon }: {
    label: string;
    value: string;
    options: string[];
    onChange: (value: string) => void;
    placeholder?: string;
    icon?: React.ReactNode;
  }) => (
    <div className="space-y-1.5">
      <label className="flex items-center space-x-1.5 text-xs font-semibold text-slate-700">
        {icon && <span className="text-slate-500">{icon}</span>}
        <span>{label}</span>
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2.5 text-sm bg-white border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 appearance-none cursor-pointer hover:border-slate-300 shadow-sm"
        >
          <option value="">{placeholder || `Select ${label}`}</option>
          {options.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
        <div className="absolute inset-y-0 right-0 flex items-center px-2.5 pointer-events-none">
          <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
    </div>
  );

  // Modern counter component for numeric values
  const ModernCounter = ({ label, value, onChange, icon, min = 0, max = 99 }: {
    label: string;
    value: number;
    onChange: (value: number) => void;
    icon?: React.ReactNode;
    min?: number;
    max?: number;
  }) => (
    <div className="space-y-1.5">
      <label className="flex items-center space-x-1.5 text-xs font-semibold text-slate-700">
        {icon && <span className="text-slate-500">{icon}</span>}
        <span>{label}</span>
      </label>
      <div className="flex items-center space-x-2">
        <button
          type="button"
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
          className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-slate-300 bg-white hover:bg-slate-50 hover:border-slate-400 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-slate-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm"
        >
          <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M20 12H4" />
          </svg>
        </button>
        <div className="flex-1 flex items-center justify-center h-10 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg">
          <span className="text-xl font-bold text-blue-900">{value}</span>
        </div>
        <button
          type="button"
          onClick={() => onChange(Math.min(max, value + 1))}
          disabled={value >= max}
          className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-blue-400 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:from-slate-300 disabled:to-slate-300 disabled:border-slate-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-lg shadow-blue-500/25"
        >
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>
    </div>
  );

  // Modern toggle switch component
  const ModernToggle = ({ label, value, onChange, icon }: {
    label: string;
    value: boolean;
    onChange: (value: boolean) => void;
    icon?: React.ReactNode;
  }) => (
    <div className="space-y-1.5">
      <label className="flex items-center space-x-1.5 text-xs font-semibold text-slate-700">
        {icon && <span className="text-slate-500">{icon}</span>}
        <span>{label}</span>
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={() => onChange(!value)}
          className={`relative inline-flex h-6 w-12 items-center justify-center rounded-full border-2 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
            value 
              ? 'bg-gradient-to-r from-blue-500 to-blue-600 border-blue-500 shadow-lg shadow-blue-500/25' 
              : 'bg-slate-200 border-slate-300 hover:bg-slate-300'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform duration-300 ${
              value ? 'translate-x-2' : '-translate-x-2'
            }`}
          />
        </button>
        <div className="mt-1">
          <span className={`text-xs font-medium ${value ? 'text-blue-600' : 'text-slate-500'}`}>
            {value ? 'Yes' : 'No'}
          </span>
        </div>
      </div>
    </div>
  );

  // Modern button group for frame materials
  const ModernButtonGroup = ({ label, value, options, onChange, icon }: {
    label: string;
    value: string;
    options: Array<{value: string; label: string}>;
    onChange: (value: string) => void;
    icon?: React.ReactNode;
  }) => (
    <div className="space-y-1.5">
      <label className="flex items-center space-x-1.5 text-xs font-semibold text-slate-700">
        {icon && <span className="text-slate-500">{icon}</span>}
        <span>{label}</span>
      </label>
      <div className="flex rounded-lg border-2 border-slate-200 bg-slate-50 p-0.5 transition-all duration-200">
        {options.map((option, index) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={`flex-1 px-2.5 py-2 text-xs font-medium rounded-md transition-all duration-200 ${
              value === option.value
                ? 'bg-white text-blue-600 shadow-md shadow-blue-500/10 border border-blue-200'
                : 'text-slate-600 hover:text-slate-800 hover:bg-white/50'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );

  const frameMaterialOptions = [
    { value: 'pvc', label: 'PVC' },
    { value: 'wood', label: 'Wood' },
    { value: 'metal', label: 'Metal' }
  ];

  if (!isOpen) return null;

  const modalContent = (
    <div className={`fixed inset-0 bg-gradient-to-br from-slate-900/80 via-slate-800/70 to-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 ${isFullscreen ? 'z-[10003]' : 'z-[999998]'}`}>
      <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-hidden border border-white/20">
        {/* Modern Header with Gradient */}
        <div className="relative bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2.5">
              <div className="p-1.5 bg-white/20 rounded-lg backdrop-blur-sm">
                {step === 1 ? (
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                ) : (
                  <Camera className="w-5 h-5 text-white" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">
                  {step === 1 ? 'Window Details' : 'Window Photos'}
                </h3>
                <p className="text-blue-100 text-xs">
                  {step === 1 ? 'Configure specifications' : 'Capture window images'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="bg-white/20 text-white px-2.5 py-1 rounded-lg text-xs font-semibold backdrop-blur-sm">
                Floor {windowElement?.floor}
              </span>
            </div>
          </div>
          {/* Step Indicator */}
          <div className="flex items-center space-x-2 mt-3">
            <div className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${
              step >= 1 ? 'bg-white' : 'bg-white/30'
            }`}></div>
            <div className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${
              step >= 2 ? 'bg-white' : 'bg-white/30'
            }`}></div>
          </div>
          {/* Decorative gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/0 via-white/5 to-indigo-600/0 pointer-events-none"></div>
        </div>

        {/* Content Area */}
        <div className="px-5 py-5 space-y-5 max-h-[calc(85vh-180px)] overflow-y-auto">
          {/* Step 1: Window Configuration */}
          {step === 1 && (
            <>
              {/* Dimensions Section */}
              <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
            <h4 className="text-xs font-bold text-slate-800 mb-3 flex items-center">
              <svg className="w-3.5 h-3.5 mr-1.5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
              Dimensions
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label htmlFor="window-width" className="flex items-center space-x-1.5 text-xs font-semibold text-slate-700">
                  <svg className="w-3 h-3 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                  </svg>
                  <span>Width (m)</span>
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  id="window-width"
                  value={width}
                  onChange={(e) => setWidth(formatDecimalInput(e.target.value))}
                  placeholder="1.20"
                  min="0.1"
                  max="10"
                  step="0.01"
                  className="w-full px-3 py-2.5 text-sm bg-white border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 hover:border-slate-300 shadow-sm"
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="window-height" className="flex items-center space-x-1.5 text-xs font-semibold text-slate-700">
                  <svg className="w-3 h-3 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7l4-4m0 0l4 4m-4-4v18" />
                  </svg>
                  <span>Height (m)</span>
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  id="window-height"
                  value={height}
                  onChange={(e) => setHeight(formatDecimalInput(e.target.value))}
                  placeholder="1.10"
                  min="0.1"
                  max="10"
                  step="0.01"
                  className="w-full px-3 py-2.5 text-sm bg-white border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 hover:border-slate-300 shadow-sm"
                />
              </div>
            </div>
          </div>


          {/* Glazing Information */}
          <div className="space-y-3">
            <ModernDropdown
              label="Glazing Type"
              value={glazingType}
              options={glazingTypeOptions}
              onChange={setGlazingType}
              placeholder="Select glazing type"
              icon={<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>}
            />
            
            <ModernDropdown
              label="Glazing Gap"
              value={glazingGap}
              options={glazingGapOptions}
              onChange={setGlazingGap}
              placeholder="Select glazing gap"
              icon={<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>}
            />
          </div>

          {/* Frame Material */}
          <ModernButtonGroup
            label="Frame Material"
            value={windowType}
            options={frameMaterialOptions}
            onChange={(value) => setWindowType(value as 'pvc' | 'wood' | 'metal' | '')}
            icon={<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>}
          />

          {/* Properties */}
          <div className="grid grid-cols-2 gap-3">
            <ModernCounter
              label="Trickle Vents"
              value={trickleVents}
              onChange={setTrickleVents}
              min={0}
              max={20}
              icon={<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>}
            />
            <ModernToggle
              label="Draught Proofed"
              value={isDraughtProofed}
              onChange={setIsDraughtProofed}
              icon={<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>}
            />
          </div>

          {/* Orientation Section */}
          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-3 border border-slate-200">
            <div className="flex items-center space-x-1.5 mb-3">
              <svg className="w-3.5 h-3.5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 013.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              <span className="text-xs font-bold text-slate-800">Orientation</span>
            </div>

            <div className="grid grid-cols-4 gap-1.5">
              {orientations.map((orientation) => (
                <button
                  key={orientation}
                  type="button"
                  onClick={() => setSelectedOrientation(orientation)}
                  className={`px-2.5 py-2 text-xs font-semibold rounded-lg border-2 transition-all duration-200 ${
                    selectedOrientation === orientation
                      ? 'bg-blue-500 text-white border-blue-500 shadow-lg shadow-blue-500/25'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {orientation}
                </button>
              ))}
            </div>
          </div>
            </>
          )}

          {/* Step 2: Photo Capture */}
          {step === 2 && (
            <>
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  Capture Window Images
                </h4>
                <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                  <p className="text-sm text-blue-800">
                    📸 Take photos of the window including glazing details and frame condition. This helps document window specifications.
                  </p>
                </div>
              </div>

              {/* Photo Capture Component */}
              <PhotoCapture
                onPhotosCaptured={handlePhotosCaptured}
                buttonLabel="Take Photos"
                maxPhotos={10}
              />

              {/* Captured Images Preview */}
              {photos.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    Captured Images ({photos.length})
                  </h4>
                  <button
                    onClick={() => setIsGalleryOpen(true)}
                    className="relative w-full aspect-video rounded-xl overflow-hidden border-2 border-blue-200 hover:border-blue-400 transition-all group"
                  >
                    <img
                      src={photos[0].url}
                      alt="Window"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-all flex items-center justify-center">
                      <div className="text-white text-center">
                        <div className="text-2xl font-bold">{photos.length}</div>
                        <div className="text-sm">Click to view all</div>
                      </div>
                    </div>
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Modern Action Bar */}
        <div className="px-5 py-4 bg-slate-50 border-t border-slate-200">
          {step === 1 ? (
            <div className="flex justify-between items-center">
              <button
                onClick={handleDelete}
                className="inline-flex items-center px-3 py-2 text-xs font-semibold text-red-600 bg-red-50 border-2 border-red-200 rounded-lg hover:bg-red-100 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500/20 transition-all duration-200"
              >
                <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete
              </button>
              <div className="flex space-x-2">
                <button
                  onClick={onClose}
                  className="px-3 py-2 text-xs font-semibold text-slate-700 bg-white border-2 border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500/20 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleNextStep}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-500 rounded-lg hover:from-blue-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20 shadow-lg shadow-blue-500/25 transition-all duration-200"
                >
                  Next
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex justify-between items-center">
              <button
                onClick={() => setStep(1)}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-700 bg-white border-2 border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500/20 transition-all duration-200"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                Back
              </button>
              <div className="flex space-x-2">
                <button
                  onClick={handleSkip}
                  className="px-3 py-2 text-xs font-semibold text-slate-700 bg-white border-2 border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500/20 transition-all duration-200"
                >
                  Skip
                </button>
                <button
                  onClick={handleSave}
                  className={`px-4 py-2 text-xs font-semibold rounded-lg focus:outline-none focus:ring-2 shadow-lg transition-all duration-200 ${
                    photos.length > 0
                      ? 'text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 focus:ring-green-500/20 shadow-green-500/25'
                      : 'text-white bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 focus:ring-blue-500/20 shadow-blue-500/25'
                  }`}
                >
                  Save {photos.length > 0 && `(${photos.length})`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Photo Gallery Modal */}
      {isGalleryOpen && (
        <PhotoGalleryModal
          isOpen={isGalleryOpen}
          onClose={() => setIsGalleryOpen(false)}
          photos={photos.map(photo => ({
            id: photo.id,
            url: photo.url,
            localPath: photo.localPath,
            fileName: photo.fileName
          }))}
          sectionTitle="Window Images"
          onDeletePhoto={handleDeletePhoto}
          onUpdateNote={handleUpdateNote}
          zIndex={10000001}
        />
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default WindowDimensionPrompt; 